import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { AlertTriangle, Trash2, ShieldAlert, RotateCcw, FileText, Wrench, Truck, Inbox, ClipboardList, Building2, Store } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const CUSTOMER_RESOURCES = [
  { key: 'invoices',  label: 'Invoices',           icon: FileText,       desc: 'All customer + supplier invoices' },
  { key: 'repairs',   label: 'Repairs',            icon: Wrench,         desc: 'Repair events + timelines' },
  { key: 'trucks',    label: 'Trucks',             icon: Truck,          desc: 'Fleet records for this customer' },
  { key: 'intakes',   label: 'Customer Intakes',   icon: Inbox,          desc: 'Their pending applications' },
  { key: 'pm_dot',    label: 'PM / DOT Schedules', icon: ClipboardList,  desc: 'Preventive maintenance + DOT' },
  { key: 'customer',  label: 'Customer Record',    icon: Building2,      desc: 'The customer itself (run last)' },
];

const GLOBAL_RESOURCES = [
  { key: 'vendors',   label: 'All Vendors',        icon: Store,          desc: 'Wipes every vendor in the system' },
];

export default function DataReset() {
  const { user, token } = useAuth();
  const isMasterAdmin = user?.role === 'master_admin';

  const [mode, setMode] = useState('customer'); // 'customer' | 'global'
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [selectedResources, setSelectedResources] = useState([]);
  const [preview, setPreview] = useState(null);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [executing, setExecuting] = useState(false);
  const [archives, setArchives] = useState([]);

  useEffect(() => {
    if (!isMasterAdmin) return;
    fetchCustomers();
    fetchArchives();
  }, [isMasterAdmin]);

  const fetchCustomers = async () => {
    const res = await fetch(`${API}/api/data-reset/customers`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const data = await res.json();
      setCustomers(data.customers || []);
    }
  };

  const fetchArchives = async () => {
    const res = await fetch(`${API}/api/data-reset/archive`, { headers: { Authorization: `Bearer ${token}` } });
    if (res.ok) {
      const data = await res.json();
      setArchives(data.batches || []);
    }
  };

  const toggleResource = (key) => {
    setSelectedResources((prev) => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (mode === 'customer' && !selectedCustomer) {
      toast.error('Pick a customer first');
      return;
    }
    if (selectedResources.length === 0) {
      toast.error('Select at least one resource');
      return;
    }
    try {
      const res = await fetch(`${API}/api/data-reset/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          scope: mode,
          customer_id: mode === 'customer' ? selectedCustomer : null,
          resources: selectedResources,
        }),
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Preview failed');
      }
      const data = await res.json();
      setPreview(data);
      setShowConfirm(true);
      setConfirmText('');
      setPassword('');
    } catch (e) {
      toast.error(e.message || 'Could not generate preview');
    }
  };

  const handleExecute = async () => {
    if (confirmText.trim() !== preview.confirmation_text_required) {
      toast.error(`Type exactly: ${preview.confirmation_text_required}`);
      return;
    }
    if (!password) {
      toast.error('Enter your master admin password');
      return;
    }
    setExecuting(true);
    try {
      const res = await fetch(`${API}/api/data-reset/execute`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          scope: mode,
          customer_id: mode === 'customer' ? selectedCustomer : null,
          resources: selectedResources,
          password,
          confirmation_text: confirmText.trim(),
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      toast.success(`✅ Reset complete — ${data.total_deleted} records archived. Restorable for 30 days.`);
      setShowConfirm(false);
      setSelectedResources([]);
      setSelectedCustomer('');
      setPreview(null);
      setConfirmText('');
      setPassword('');
      fetchArchives();
    } catch (e) {
      toast.error(e.message || 'Reset failed');
    } finally {
      setExecuting(false);
    }
  };

  const handleRestore = async (batchId, label) => {
    if (!window.confirm(`Restore ${label}? This will move archived records back to the live database.`)) return;
    try {
      const res = await fetch(`${API}/api/data-reset/restore/${batchId}`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const e = await res.json().catch(() => ({}));
        throw new Error(e.detail || 'Restore failed');
      }
      toast.success('Records restored from archive');
      fetchArchives();
    } catch (e) {
      toast.error(e.message || 'Restore failed');
    }
  };

  if (!isMasterAdmin) {
    return (
      <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <CardContent className="text-center py-12">
          <ShieldAlert className="mx-auto mb-3" size={48} style={{ color: 'var(--text-muted)' }} />
          <p style={{ color: 'var(--text-primary)' }}>Master Admin only.</p>
          <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
            This destructive operation requires master_admin privileges.
          </p>
        </CardContent>
      </Card>
    );
  }

  const activeResources = mode === 'customer' ? CUSTOMER_RESOURCES : GLOBAL_RESOURCES;

  return (
    <div className="space-y-6" data-testid="data-reset-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
          <AlertTriangle size={26} className="text-red-500" />
          Danger Zone — Data Reset
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>
          Permanently delete operational data. Records are archived for 30 days before purge.
        </p>
      </div>

      {/* Big red warning banner */}
      <div className="rounded-lg p-4 flex items-start gap-3"
           style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.3)' }}>
        <AlertTriangle size={20} className="text-red-500 shrink-0 mt-0.5" />
        <div className="text-sm" style={{ color: 'var(--text-primary)' }}>
          <strong className="text-red-400">This is destructive.</strong> Selected records will be moved to a soft-delete
          archive immediately. After {30} days they are permanently purged. Every reset is logged to Slack and the Admin Audit log.
          Master admin users, internal staff accounts, security events, and audit logs are <em>never</em> touched.
        </div>
      </div>

      {/* Mode selector */}
      <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <CardHeader>
          <CardTitle className="text-base" style={{ color: 'var(--text-primary)' }}>1. Choose reset scope</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2">
            <Button
              variant={mode === 'customer' ? 'default' : 'outline'}
              onClick={() => { setMode('customer'); setSelectedResources([]); setPreview(null); }}
              data-testid="mode-customer"
            >
              Specific Customer
            </Button>
            <Button
              variant={mode === 'global' ? 'default' : 'outline'}
              onClick={() => { setMode('global'); setSelectedResources([]); setPreview(null); setSelectedCustomer(''); }}
              data-testid="mode-global"
            >
              Global (Vendors)
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Customer picker (only in customer mode) */}
      {mode === 'customer' && (
        <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
          <CardHeader>
            <CardTitle className="text-base" style={{ color: 'var(--text-primary)' }}>2. Pick a customer</CardTitle>
          </CardHeader>
          <CardContent>
            <select
              value={selectedCustomer}
              onChange={(e) => { setSelectedCustomer(e.target.value); setPreview(null); }}
              className="w-full h-10 px-3 rounded-md border text-sm"
              style={{ background: 'var(--bg)', borderColor: 'var(--border)', color: 'var(--text-primary)' }}
              data-testid="customer-picker"
            >
              <option value="">— Select a customer —</option>
              {customers.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.company_name} {c.tenant_group ? `(${c.tenant_group})` : ''} {!c.is_active ? '— INACTIVE' : ''}
                </option>
              ))}
            </select>
          </CardContent>
        </Card>
      )}

      {/* Resource picker */}
      <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <CardHeader>
          <CardTitle className="text-base" style={{ color: 'var(--text-primary)' }}>
            {mode === 'customer' ? '3.' : '2.'} Select what to delete
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {activeResources.map((r) => {
              const Icon = r.icon;
              const checked = selectedResources.includes(r.key);
              return (
                <label
                  key={r.key}
                  className="flex items-start gap-3 p-3 rounded-lg cursor-pointer"
                  style={{
                    background: checked ? 'rgba(239, 68, 68, 0.08)' : 'var(--bg)',
                    border: `1px solid ${checked ? 'rgba(239, 68, 68, 0.3)' : 'var(--border)'}`,
                  }}
                  data-testid={`resource-${r.key}`}
                >
                  <Checkbox checked={checked} onCheckedChange={() => toggleResource(r.key)} className="mt-0.5" />
                  <Icon size={18} className="mt-0.5 shrink-0" style={{ color: 'var(--text-muted)' }} />
                  <div>
                    <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>{r.label}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{r.desc}</p>
                  </div>
                </label>
              );
            })}
          </div>

          <div className="flex justify-end mt-4">
            <Button
              variant="destructive"
              onClick={handlePreview}
              disabled={selectedResources.length === 0 || (mode === 'customer' && !selectedCustomer)}
              data-testid="preview-btn"
              className="gap-2"
            >
              <Trash2 size={16} />
              Review Deletion
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Archive list */}
      <Card style={{ background: 'var(--surface)', border: '1px solid var(--border)' }}>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2" style={{ color: 'var(--text-primary)' }}>
            <RotateCcw size={16} /> Recent Resets (Restorable)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {archives.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No resets have been performed yet.</p>
          ) : (
            <div className="space-y-2">
              {archives.map((b) => (
                <div key={b.id} className="flex items-center justify-between p-3 rounded-lg"
                     style={{ background: 'var(--bg)', border: '1px solid var(--border)' }}>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-sm" style={{ color: 'var(--text-primary)' }}>
                        {b.customer_name || 'GLOBAL VENDORS'}
                      </p>
                      <Badge className="bg-red-500/15 text-red-400">{b.total_deleted} records</Badge>
                      {b.restored && <Badge className="bg-green-500/15 text-green-400">Restored</Badge>}
                    </div>
                    <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                      by {b.executed_by_email} · {new Date(b.executed_at).toLocaleString()} ·
                      Restorable until {new Date(b.expires_at).toLocaleDateString()} ·
                      Resources: {b.resources.join(', ')}
                    </p>
                  </div>
                  {b.restorable && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleRestore(b.id, b.customer_name || 'global vendors')}
                      data-testid={`restore-${b.id}`}
                      className="gap-1.5"
                    >
                      <RotateCcw size={14} /> Restore
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Confirmation modal */}
      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent className="max-w-lg" data-testid="reset-confirm-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-400">
              <AlertTriangle size={22} /> Confirm Permanent Deletion
            </DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-4 py-2">
              <div className="rounded-lg p-3"
                   style={{ background: 'rgba(239, 68, 68, 0.08)', border: '1px solid rgba(239, 68, 68, 0.25)' }}>
                <p className="text-sm font-medium mb-2" style={{ color: 'var(--text-primary)' }}>
                  About to delete from {preview.customer_name ? <strong>{preview.customer_name}</strong> : <strong>ALL VENDORS</strong>}:
                </p>
                <ul className="text-sm space-y-1" style={{ color: 'var(--text-muted)' }}>
                  {Object.entries(preview.counts).map(([k, v]) => (
                    <li key={k}>• <strong style={{ color: 'var(--text-primary)' }}>{v}</strong> {k}</li>
                  ))}
                </ul>
                <p className="text-xs mt-3" style={{ color: 'var(--text-muted)' }}>
                  Total <strong>{preview.total_records}</strong> records will be moved to soft-delete archive
                  (restorable for {preview.archive_retention_days} days).
                </p>
              </div>

              <div>
                <Label htmlFor="confirm_text">
                  Type <code className="px-1.5 py-0.5 rounded text-red-400" style={{ background: 'var(--bg)' }}>
                    {preview.confirmation_text_required}
                  </code> to confirm
                </Label>
                <Input
                  id="confirm_text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder={preview.confirmation_text_required}
                  className="mt-1.5 font-mono"
                  data-testid="confirm-text-input"
                />
              </div>

              <div>
                <Label htmlFor="password">Your master admin password</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="mt-1.5"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirm(false)} disabled={executing}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleExecute}
              disabled={executing || confirmText.trim() !== preview?.confirmation_text_required || !password}
              data-testid="confirm-execute-btn"
              className="gap-2"
            >
              <Trash2 size={16} />
              {executing ? 'Deleting…' : 'Permanently Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
